// SNOWCLUB-V101-STAGING-A1 — THE DISPLAYED DESIGN CART → A SERVER-PRICED ORDER REQUEST.
//
// The design's Card Details "Pay $X" is the live pay seam on /mountain-<m>/booking/review. What it pays
// for is exactly the cart the design displays, which the design itself mirrors into `sg-cart-v1` on every
// change (SG-CART-PERSIST-023). This module turns those lines into the request `/api/orders/design-cart`
// prices on the server — and it is deliberately NARROW:
//
//   · A line maps EXACTLY or the whole payment is refused, by name. No line is dropped, re-priced or
//     stood in for by a guess; a partial order would charge for less than the customer agreed to.
//   · No price is sent for any line. The one money figure in the request is `clientTotal` — the total
//     the design displayed, computed with the design's own arithmetic (qty × price) — and the backend
//     only COMPARES it with its own sum and refuses on mismatch (order.ts `createOrderFromCustomerCart`).
//   · Transport legs travel as the engine's schedule id plus what the customer SAW (operator, clock,
//     stop, date). The server re-reads the live board, finds the row by that id, and refuses if the row
//     no longer says what was displayed (`@/lib/design-order-resolve`).
//
// Refused on purpose, each with a sentence the design prints on its own notice banner:
//   · a leg with no schedule id / no Flexible flag / no searched corridor (picked before this seam existed)
//   · a driver tip — the order has no line for it, and charging less than "Pay $X" said is not allowed
//   · booking-store extras — they are the legacy engine's STORE rows, with no SnowClub product behind them
//   · a marketplace line with no backend product (`listingId`) on the host cart item it was displayed from
//   · a multi-day line the backend quote does not prove is a per-day rental — the planner carries its dates and day count so the
//     quote can be asked, and `quoteDesignOrder` refuses it after the authoritative answer unless that line came back with the
//     vendor's public rental terms for exactly those dates (Ski Butlers Whistler, `@/lib/rental-terms`). Other products stay per unit.

import type { DesignCartLine } from "@/components/marketplace/DesignCartLines";
import type { CartItem } from "@/lib/cart-store";

/** One design-held transport leg as the server needs it to re-derive the departure. */
export interface DesignOrderTransport {
  lineId: string;
  /** the engine's schedule id for the row the customer picked (`hostedRidesFromResult` → `sgSid: d.id`) */
  sgSid: string;
  leg: "depart" | "return";
  tripId?: string;
  /** what the design DISPLAYED — verified server-side against the board row, never trusted */
  vendor: string;
  dep: string;
  depStop: string;
  /** the date this leg travels (the board is read on it) */
  date: string;
  flexi: boolean;
  who: "guest" | "member";
  quantity: number;
  /** the corridor the board was searched on for THIS leg, e.g. vancouver → whistler */
  departureCode: string;
  arrivalCode: string;
  adult: number;
}

export interface DesignOrderProduct {
  lineId: string;
  productDocumentId: string;
  quantity: number;
  useStartDate?: string;
  useEndDate?: string;
  /** the design's own day count, present only above one day; never forwarded, only checked against the quote's rental terms */
  days?: number;
}

export interface DesignOrderRequest {
  mountainCode: string;
  /** the total the design displayed — an assertion the backend checks, never an amount it charges */
  clientTotal: number;
  transport: DesignOrderTransport[];
  products: DesignOrderProduct[];
}

/** The resolver's existing submission order, shared with refusal presentation so an index never names a displayed row by accident. */
export const orderedDesignTransport = (legs: DesignOrderTransport[]) =>
  [...legs.filter((l) => l.leg === "depart"), ...legs.filter((l) => l.leg === "return")];

export const TRANSPORT_BOOKING_NOTICE = "Book at least 3 hours before departure, or at least 24 hours for Epic Rides. Some departures require earlier booking. Availability is checked again at checkout.";

const TRANSPORT_REFUSAL_COPY: Record<string, string> = {
  transport_departure_past: "This departure has already left. Choose another departure or remove this item from your cart.",
  transport_departure_too_soon: `This departure is too close to book. ${TRANSPORT_BOOKING_NOTICE} Choose another departure or remove this item from your cart.`,
  transport_departure_time_unknown: "The departure time could not be confirmed. Choose another departure or remove this item from your cart.",
  transport_departure_rule_unavailable: "The booking deadline could not be confirmed. Try again later, or choose another departure or remove this item from your cart.",
};

/** Only the accepted backend HTTP400 contract; no inference from a message, operator label or browser clock. */
export function transportRefusalCopy(status: number, refusal: unknown): string | null {
  return status === 400 && typeof refusal === "string" && Object.prototype.hasOwnProperty.call(TRANSPORT_REFUSAL_COPY, refusal)
    ? TRANSPORT_REFUSAL_COPY[refusal] ?? null : null;
}

/** Real submitted transport index only. Products, missing indexes and ambiguous line identities remain whole-cart errors. */
export function transportRefusalLine(request: Pick<DesignOrderRequest, "transport" | "products">, index: unknown): DesignOrderTransport | null {
  const ordered = orderedDesignTransport(request.transport);
  const ids = [...request.transport, ...request.products].map((l) => l.lineId);
  if (ordered.length !== request.transport.length || ids.some((id) => typeof id !== "string" || !id.trim()) || new Set(ids).size !== ids.length) return null;
  return typeof index === "number" && Number.isSafeInteger(index) && index >= 0 && index < ordered.length ? ordered[index] ?? null : null;
}

export type DesignOrderPlan =
  | { ok: true; request: DesignOrderRequest; fingerprint: string }
  | { ok: false; reason: string; /** true only while the live catalogue has not yet answered: not a refusal, the plan is asked again when it lands */ pending?: true };

/**
 * Does pricing this cart need the live store catalogue? Only a non-transport line with no host cart item of the same id resolves
 * through the feed; a transport-only cart (or one fully joined to host items) never waits on it.
 */
export function cartNeedsCatalogue(lines: DesignCartLine[], hostItems: CartItem[]): boolean {
  return lines.some((l) => l.cat !== "Transport" && !hasStoreStamp(l) && !hostItems.some((h) => h.id === l.id && (h.section ?? "marketplace") !== "transport"));
}

/**
 * A live-feed product added after the feed published its native identity carries `store-<id>`, the mountain AND the listing
 * documentId on the line itself (SG_LIVE_STORE_LINE). It needs no second catalogue fetch to be planned; an older line lacks
 * `listingId` and keeps resolving through the same-environment feed.
 */
function hasStoreStamp(l: DesignCartLine): boolean {
  return l.id.startsWith("store-") && nonEmpty(l.mountainCode) && nonEmpty(l.listingId);
}

export interface StorePick { id?: string; quantity?: number }

/**
 * A row of the live store feed (`SG_STORE_LOAD` → `/api/legacy/store`, `window.__SG_STORE.store`) as far as
 * identity goes. `listingId` is the BE listing documentId the server joined EXACTLY on the importer's
 * `legacyId = "LEGACY-STORE-" + id` (store-listing-identity.ts); absent when that join was missing or ambiguous.
 */
export interface StoreIdentityRow { id?: string | number; listingId?: string | null; mountainCode?: string | null }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CLOCK_24 = /^\d{2}:\d{2}$/;
const NOTHING = "Nothing was charged.";

const nonEmpty = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";
/** The first value that is a non-blank string — a blank field is absent, not a value. */
const firstOf = (...vs: unknown[]): string => (vs.find(nonEmpty) ?? "").trim();
const label = (l: DesignCartLine) => firstOf(l.name, l.legName, l.vendor, "this item");
const whole = (v: unknown) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** The design's own day count (SG_LINE_DAYS), never a price: a line above one day must be proven a per-day rental by the quote. */
function spanDays(l: DesignCartLine & { startDate?: string; endDate?: string }): number {
  const start = firstOf(l.start, l.startDate, l.date);
  const end = firstOf(l.end, l.endDate);
  if (!ISO_DATE.test(start) || !ISO_DATE.test(end)) return 1;
  const ms = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
  return Number.isFinite(ms) && ms >= 0 ? Math.floor(ms / 86_400_000) + 1 : 1;
}

function transportLine(l: DesignCartLine): { ok: true; t: DesignOrderTransport } | { ok: false; reason: string } {
  const again = `The ${firstOf(l.legName).toLowerCase() || "departure"} "${label(l)}" can't be paid by card as it was picked — please pick it again from the schedule. ${NOTHING}`;
  const flexi = (l as DesignCartLine & { flexi?: unknown }).flexi;
  const qty = whole(l.qty);
  const leg = l.legKind === "DEP" || l.legKind === "DEPART" ? "depart" : l.legKind === "RET" || l.legKind === "RETURN" ? "return" : null;
  if (
    l.sgSid === undefined || l.sgSid === null || String(l.sgSid).trim() === "" ||
    typeof flexi !== "boolean" ||
    !leg || !qty ||
    !nonEmpty(l.vendor) || !nonEmpty(l.depStop) ||
    typeof l.dep !== "string" || !CLOCK_24.test(l.dep) ||
    typeof l.date !== "string" || !ISO_DATE.test(l.date) ||
    !nonEmpty(l.departureCode) || !nonEmpty(l.arrivalCode)
  ) {
    return { ok: false, reason: again };
  }
  const adult = whole(l.adult) ?? qty;
  return {
    ok: true,
    t: {
      lineId: l.id,
      sgSid: String(l.sgSid),
      leg,
      ...(nonEmpty(l.tripId) ? { tripId: l.tripId } : {}),
      vendor: l.vendor,
      dep: l.dep,
      depStop: l.depStop,
      date: l.date,
      flexi,
      who: l.who === "member" ? "member" : "guest",
      quantity: qty,
      departureCode: l.departureCode,
      arrivalCode: l.arrivalCode,
      adult,
    },
  };
}

/**
 * A marketplace product the design wrote itself from the live store feed: id `store-<liveRowId>`, stamped with the
 * mountain it was added on. Its identity is the feed row with EXACTLY that id and the listingId the server joined onto
 * it — never a name, price or position. A booking-store extra uses the same `store-` prefix but is never stamped with a
 * mountain, so it does not pass for a product here (and `storePicks` still refuses the ones the engine reports).
 */
function storeProductIdentity(l: DesignCartLine, mountainCode: string, storeRows: StoreIdentityRow[]): { ok: true; listingId: string } | { ok: false } {
  if (!l.id.startsWith("store-")) return { ok: false };
  const raw = l.id.slice("store-".length);
  const rows = storeRows.filter((r) => r?.id !== undefined && String(r.id) === raw);
  const owner = resolveStoreLineMountain(l, storeRows);
  if (!owner || owner !== mountainCode) return { ok: false };
  if (hasStoreStamp(l)) {
    // the line's own native identity stands unless the feed, when it holds this id, is ambiguous or says otherwise
    if (rows.length > 1) return { ok: false };
    if (rows.length === 1 && nonEmpty(rows[0]?.listingId) && rows[0]?.listingId !== l.listingId) return { ok: false };
    return { ok: true, listingId: firstOf(l.listingId) };
  }
  const listingId = rows.length === 1 ? rows[0]?.listingId : undefined;
  return nonEmpty(listingId) ? { ok: true, listingId } : { ok: false };
}

/**
 * THE MOUNTAIN A LIVE-FEED PRODUCT LINE BELONGS TO - one answer for the quote, the order and the cart owner.
 *   · the line's own recorded stamp, exactly, when it has one (an explicit foreign mountain is never overwritten) - unless the unique
 *     feed row for that exact id names a DIFFERENT mountain, which is a contradiction and resolves to nothing;
 *   · otherwise (an older line added before the stamp existed) the mountain the SAME-ENVIRONMENT live store feed attributes to that
 *     exact stable row id - only when exactly one feed row carries that id and it names a mountain.
 * Never the page route, a name, a title, an amount or a position. Anything else is "" (unresolved -> refused by name).
 */
export function resolveStoreLineMountain(l: DesignCartLine, storeRows: StoreIdentityRow[]): string {
  if (!l.id.startsWith("store-")) return "";
  const raw = l.id.slice("store-".length);
  const rows = storeRows.filter((r) => r?.id !== undefined && String(r.id) === raw);
  const feedOwner = rows.length === 1 ? rows[0]?.mountainCode : undefined;
  const fromFeed = nonEmpty(feedOwner) ? feedOwner : "";
  if (nonEmpty(l.mountainCode)) {
    // a recorded owner stands, but never against the authoritative feed row for that exact id: a contradiction is unresolved
    return fromFeed && fromFeed !== l.mountainCode ? "" : l.mountainCode;
  }
  return fromFeed;
}

function productLine(l: DesignCartLine, hostItems: CartItem[], mountainCode: string, storeRows: StoreIdentityRow[]): { ok: true; p: DesignOrderProduct } | { ok: false; reason: string } {
  // THE ONLY JOINS: the design line was built from the host cart item with this exact id
  // (ExportCheckout: `id: i.id`), or from the live store row with this exact id (storeProductIdentity).
  // No name or position matching — that is how the wrong product is sold.
  const host = hostItems.find((h) => h.id === l.id && (h.section ?? "marketplace") !== "transport");
  const store = host ? null : storeProductIdentity(l, mountainCode, storeRows);
  if (store?.ok) {
    const qty = whole(l.qty);
    if (!qty) return { ok: false, reason: `"${label(l)}" has no valid quantity. ${NOTHING}` };
    const days = spanDays(l);
    const date = firstOf(l.start, l.date);
    const end = firstOf(l.end, (l as { endDate?: string }).endDate);
    if ((date && !ISO_DATE.test(date)) || (end && !ISO_DATE.test(end)) || (days > 1 && (!date || !end))) {
      return { ok: false, reason: `"${label(l)}" has a date that can't be confirmed. Choose the date again. ${NOTHING}` };
    }
    return {
      ok: true,
      p: {
        lineId: l.id,
        productDocumentId: store.listingId,
        quantity: qty,
        ...(date ? { useStartDate: date } : {}),
        ...(date && end ? { useEndDate: end } : {}),
        ...(days > 1 ? { days } : {}),
      },
    };
  }
  if (!host || !nonEmpty(host.listingId)) {
    return {
      ok: false,
      reason: `"${label(l)}" can't be paid by card yet — it isn't linked to a SnowClub product. Remove it to continue. ${NOTHING}`,
    };
  }
  const qty = whole(l.qty);
  if (!qty) return { ok: false, reason: `"${label(l)}" has no valid quantity. ${NOTHING}` };
  const start = nonEmpty(host.startDate) ? host.startDate : undefined;
  const end = nonEmpty(host.endDate) ? host.endDate : undefined;
  const days = spanDays(l);
  // a multi-day line is asked of the quote with the host item's own dates, and only when they span exactly the days displayed
  if (days > 1 && (!start || !end || spanDays({ ...l, start, end }) !== days)) {
    return { ok: false, reason: `"${label(l)}" has dates that can't be confirmed. Choose the dates again. ${NOTHING}` };
  }
  return {
    ok: true,
    p: {
      lineId: l.id,
      productDocumentId: host.listingId,
      quantity: qty,
      ...(start ? { useStartDate: start } : {}),
      ...(end ? { useEndDate: end } : {}),
      ...(days > 1 ? { days } : {}),
    },
  };
}

/**
 * Plan the order for the cart the design displays. Pure: no storage, no network.
 *
 * @param tip the tip the design's `sg:pay` reports (its "Pay $X" includes it)
 * @param storePicks the booking-store rows the design reports as ticked (`SG_STORE_PICKED`)
 */
export function planDesignOrder({
  mountainCode,
  lines,
  hostItems,
  tip,
  storePicks,
  storeRows = [],
}: {
  mountainCode: string;
  lines: DesignCartLine[];
  hostItems: CartItem[];
  tip: number;
  storePicks: StorePick[];
  /** the live store feed's identity rows (`window.__SG_STORE.store`); empty = no store product can be linked */
  storeRows?: StoreIdentityRow[];
}): DesignOrderPlan {
  if (!lines.length) return { ok: false, reason: `There is nothing in this cart to pay for. ${NOTHING}` };
  if (Number(tip) > 0) {
    return {
      ok: false,
      reason: `Driver tips can't be added to a card payment yet. Set the driver tip to $0 in your cart to continue — the total you pay will then match what you see. ${NOTHING}`,
    };
  }
  // `SG_STORE_PICKED` reports every `store-` cart line. A line that is a verified live-feed PRODUCT (exact feed id, listingId
  // joined by the server, hosted mountain stamp) is planned below as a product, so its pick is not an extra - provided the pick
  // carries no quantity or exactly the line's current quantity. Any other pick (unknown id, or the same id at another quantity)
  // is an extra and is refused.
  const plannedProducts = new Map<string, number | null>();
  for (const l of lines) {
    if (storeProductIdentity(l, mountainCode, storeRows).ok) plannedProducts.set(l.id.slice("store-".length), whole(l.qty));
  }
  const isPlannedProductPick = (pick: StorePick) => {
    const id = String(pick.id);
    if (!plannedProducts.has(id)) return false;
    return pick.quantity === undefined || pick.quantity === null || Number(pick.quantity) === plannedProducts.get(id);
  };
  if (storePicks.some((s) => s?.id && !isPlannedProductPick(s))) {
    return {
      ok: false,
      reason: `Booking-store extras can't be paid by card yet. Untick them in the booking store to continue. ${NOTHING}`,
    };
  }

  const transport: DesignOrderTransport[] = [];
  const products: DesignOrderProduct[] = [];
  let total = 0;
  for (const l of lines) {
    const price = Number(l.price);
    if (!Number.isFinite(price) || price < 0) return { ok: false, reason: `"${label(l)}" has no valid price. ${NOTHING}` };
    if (l.cat === "Transport") {
      const r = transportLine(l);
      if (!r.ok) return r;
      transport.push(r.t);
      total += r.t.quantity * price;
    } else {
      const r = productLine(l, hostItems, mountainCode, storeRows);
      if (!r.ok) return r;
      products.push(r.p);
      total += r.p.quantity * price * (r.p.days ?? 1);
    }
  }

  const clientTotal = Math.round(total * 100) / 100;
  // The fingerprint names the cart AND what it was planned to: a product's resolved native document id and use dates are part of it,
  // so a changed feed mapping (a feed that arrives late or is replaced) is a different question for the quote, never the same one.
  const fingerprint = [
    ...lines.map((l) => `${l.id}:${l.qty}:${l.price}:${l.date ?? ""}:${l.dep ?? ""}:${l.sgSid ?? ""}`).sort(),
    ...products.map((p) => `${p.lineId}>${p.productDocumentId}@${p.useStartDate ?? ""}~${p.useEndDate ?? ""}`).sort(),
  ].join("|");
  return { ok: true, request: { mountainCode, clientTotal, transport, products }, fingerprint };
}
